import axios from 'axios'
import { bus } from 'src/boot/bus'

import { NotifyService, AuthService } from 'src/services'
import qs from 'qs'
import { isNil } from 'lodash-es'

const CANCEL_GET_KEY = 'GET'
const CANCEL_ALL_KEY = 'AbortCurrentHTTPRequests'

export class HttpService {
  constructor({
    auth = false,
    shouldCatchError = false,
    shouldCancelOnGet = false,
    shouldCancelOnRoute = false,
    customCancelEvent,
  } = {}) {
    this.requiresAuth = auth
    this.shouldCatchError = shouldCatchError
    this.shouldCancelOnGet = shouldCancelOnGet
    this.shouldCancelOnRoute = shouldCancelOnRoute
    this.customCancelEvent = customCancelEvent
    this.cancelSource = axios.CancelToken.source()

    this.instance = axios.create({
      baseURL: process.env.VUE_APP_API_URL,
      cancelToken: this.cancelSource.token,
      paramsSerializer: (params) =>
        qs.stringify(params, { encode: false, arrayFormat: 'brackets' }),
    })
    this.addInterceptors()
    this.setupCancellationHandlers()

    return this.instance
  }

  setupCancellationHandlers() {
    const abortRequest = () => {
      this.cancelSource.cancel()
      bus.$off(CANCEL_GET_KEY, abortRequest)
      bus.$off(CANCEL_ALL_KEY, abortRequest)
      if (this.customCancelEvent) {
        bus.$off(this.customCancelEvent, abortRequest)
      }
    }

    if (this.shouldCancelOnGet) {
      bus.$on(CANCEL_GET_KEY, abortRequest)
    }
    if (this.shouldCancelOnRoute) {
      bus.$on(CANCEL_ALL_KEY, abortRequest)
    }
    if (this.customCancelEvent) {
      bus.$on(this.customCancelEvent, abortRequest)
    }
  }

  addInterceptors() {
    this.instance.interceptors.request.use((config) => {
      if (!config._retry) {
        const incrementMonth = (payload) => {
          if (!isNil(payload?.month)) {
            payload.month++
          }
        }
        incrementMonth(config.params)
        incrementMonth(config.data)
      }

      return config
    })

    this.instance.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        if (this.shouldCatchError && !axios.isCancel(error) && error?.response?.status !== 401) {
          const data = error.response?.data
          const message =
            data?.message ||
            data?.errors?.reduce((acc, e) => `${acc}<p>${e.message}</p>`, '') ||
            'An error occurred'
          NotifyService.create({
            message,
            type: 'negative',
            html: true,
            group: error.request?.responseURL,
          })
        }

        return Promise.reject(error)
      }
    )

    if (this.requiresAuth) {
      this.instance.interceptors.request.use(async (config) => {
        try {
          if (!AuthService.hasAccessToken && AuthService.isRefreshing) {
            await AuthService.waitForRefreshingEnd()
          }
        } catch (e) {}

        if (AuthService.hasAccessToken) {
          config.headers.Authorization = `Bearer  ${AuthService.accessToken.token}`
        }

        return config
      })

      this.instance.interceptors.response.use(
        (response) => response,
        async (error) => {
          if (error?.response?.status === 401) {
            try {
              await AuthService.handleAccessTokenRefresh()
              return this.instance({ ...error.config, _retry: true })
            } catch (e) {}
          }

          return Promise.reject(error)
        }
      )
    }
  }
}
